import React from "react";
import { Users, Eye, Smartphone, Monitor, Calendar, Clock } from "lucide-react";
import { formatRelativeTime } from "../../utils/helpers";

interface UserTrackingUsersProps {
  users: any[];
  isLoading: boolean;
  onViewUser: (user: any) => void;
  error?: string | null;
  pagination?: {
    current: number;
    pages: number;
    total: number;
    limit: number;
  };
  currentPage?: number;
  onPageChange?: (page: number) => void;
  showCreatedDate?: boolean;
  showUsage?: boolean;
}

const UserTrackingUsers: React.FC<UserTrackingUsersProps> = ({
  users,
  isLoading,
  onViewUser,
  error,
  pagination,
  currentPage,
  onPageChange,
  showCreatedDate = false,
  showUsage = false,
}) => {
  const getPlatformIcon = (platform: string) => {
    switch (platform?.toLowerCase()) {
      case "android":
        return <Smartphone className="h-4 w-4 text-green-600" />;
      case "ios":
        return <Smartphone className="h-4 w-4 text-blue-600" />;
      case "web":
        return <Monitor className="h-4 w-4 text-purple-600" />;
      default:
        return <Smartphone className="h-4 w-4 text-gray-600" />;
    }
  };

  const getPlatformColor = (platform: string) => {
    switch (platform?.toLowerCase()) {
      case "android":
        return "bg-green-100 text-green-800";
      case "ios":
        return "bg-blue-100 text-blue-800";
      case "web":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 5 }).map((_, index) => (
          <div
            key={index}
            className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
                <div>
                  <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-24"></div>
                </div>
              </div>
              <div className="h-8 bg-gray-200 rounded w-20"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!users || users.length === 0) {
    return (
      <div className="text-center py-12">
        <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          No users found
        </h3>
        <p className="text-gray-500">
          No user tracking data is available at the moment.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {users.map((user, index) => (
        <div
          key={user._id || index}
          className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-medium text-gray-900">
                    {user.userInfo?.firstName && user.userInfo?.lastName
                      ? `${user.userInfo.firstName} ${user.userInfo.lastName}`
                      : user.userInfo?.email || "Unknown User"}
                  </h4>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPlatformColor(
                      user.currentVersion?.platform
                    )}`}
                  >
                    {getPlatformIcon(user.currentVersion?.platform)}
                    <span className="ml-1">
                      {user.currentVersion?.platform || "Unknown"}
                    </span>
                  </span>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span>v{user.currentVersion?.version || "N/A"}</span>
                  {showCreatedDate && (
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      <span>
                        Joined{" "}
                        {formatRelativeTime(
                          user.accountCreatedDate || user.createdAt
                        )}
                      </span>
                    </div>
                  )}
                  {showUsage && (
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span>
                        Avg {user.appUsageStats?.averageSessionDuration || 0}m
                      </span>
                      <span>•</span>
                      <span>
                        Sessions {user.appUsageStats?.totalSessions || 0}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    <span>
                      Last seen{" "}
                      {formatRelativeTime(user.lastSeen || user.updatedAt)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={() => onViewUser(user)}
              className="flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <Eye className="h-4 w-4" />
              View
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default UserTrackingUsers;
